import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import { useScreenInit } from '../useScreenInit.js';
import { CheckIcon, ArrowRightIcon, CalendarIcon } from 'lucide-react';
import { trackEvent } from '../utils/analytics';
import { useLanguage } from '../components/LanguageContext';
function MinimalHeader() {
  const {
    t
  } = useLanguage();
  return <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100" data-id="element-2430">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2431">
        <Link to="/" className="flex items-center gap-3" aria-label="BCITI and ATNA — back to home" data-id="element-2432">
          <span className="flex items-center" data-id="element-2433">
            <span className="font-sans text-2xl font-bold text-black tracking-[0.2em] leading-none" data-id="element-2434">
              BCITI
            </span>
            <span className="ml-2 inline-block w-3.5 h-3.5 bg-indigo" aria-hidden="true" data-id="element-2435" />
          </span>
          <span className="h-6 w-px bg-black/20" aria-hidden="true" data-id="element-2436" />
          <span className="relative font-sans text-base font-medium text-black tracking-[0.25em] leading-none" data-id="element-2437">
            ATNA
          </span>
        </Link>
        <Link to="/" className="font-sans text-sm text-black/60 hover:text-black transition-colors" data-id="element-2438">
          {t('Back to home', "Retour à l'accueil")}
        </Link>
      </div>
    </header>;
}
const fade = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -20
  },
  transition: {
    duration: 0.4
  }
};
export function RequestDemo() {
  const screenInit = useScreenInit();
  const location = useLocation();
  const {
    t
  } = useLanguage();
  // Formspree — endpoint https://formspree.io/f/xaqgzlbg
  const [state, handleSubmit] = useForm('xaqgzlbg');
  const [form, setForm] = useState({
    firstName: '',
    email: '',
    organization: '',
    orgType: ''
  });
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // `industry` is passed by the light industry pages' "Talk to us" CTAs;
    // `vertical` is passed by ATNA assistant links. Both map to orgType.
    const industry = params.get('industry');
    const vertical = params.get('vertical');
    const typeMap: Record<string, string> = {
      // industry param values
      cities: 'cities',
      airports: 'airports',
      telecom: 'telcos',
      health: 'health',
      universities: 'universities',
      // vertical param values
      city: 'cities',
      airport: 'airports',
      telco: 'telcos',
      university: 'universities',
      brossard: 'cities'
    };
    const key = industry || vertical;
    if (key && typeMap[key]) {
      setForm(prev => ({
        ...prev,
        orgType: typeMap[key]
      }));
    }
  }, [location.search]);
  // Track a successful submission once Formspree confirms it.
  useEffect(() => {
    if (!state.succeeded) return;
    const params = new URLSearchParams(location.search);
    const source = params.get('source') || 'direct';
    trackEvent('demo_submitted', {
      source,
      vertical: form.orgType || 'unknown'
    });
  }, [state.succeeded]);
  // Masqué pour le moment : l'étape de prise de rendez-vous après un contact.
  // Le code de cette étape est conservé plus bas — passez ce flag à true pour le réactiver.
  const SHOW_BOOKING_STEP = false;
  // Screens preview: allow forcing the confirmation step via screen state.
  const submitted = state.succeeded || screenInit?.step === 1;
  // Étape RDV affichée uniquement si elle n'est pas masquée.
  const showConfirmation = submitted && SHOW_BOOKING_STEP;
  // Message court affiché à la place de l'étape RDV quand celle-ci est masquée.
  const showThanks = submitted && !SHOW_BOOKING_STEP;
  const formValid = form.firstName.trim() && form.email.trim() && form.organization.trim();
  return <div className="w-full min-h-screen bg-white" data-id="element-2439">
      <MinimalHeader data-id="element-2440" />

      <main className="pt-32 pb-24 px-6 flex flex-col items-center justify-center min-h-screen" data-id="element-2441">
        <div className="w-full max-w-5xl mx-auto" data-id="element-2442">
          <AnimatePresence mode="wait" data-id="element-2443">
            {!submitted && <motion.div key="step0" {...fade} className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start" data-id="element-2444">
                {/* Left side — Value Prop */}
                <div className="pt-4" data-id="element-2445">
                  <h1 className="font-serif text-4xl md:text-5xl font-normal text-black leading-tight mb-4" data-id="element-2446">
                    {t('Request Your Demo', 'Demandez votre démo')}
                  </h1>
                  <p className="font-sans text-lg text-black/60 mb-12" data-id="element-2447">
                    {t('Discover how BCITI and ATNA can help your organization.', 'Découvrez comment BCITI et ATNA peuvent aider votre organisation.')}
                  </p>
                  <ul className="space-y-5" data-id="element-2448">
                    {[t('City of Brossard case study', 'Étude de cas · Ville de Brossard'), t('400,000+ verified identities', 'Plus de 400 000 identités vérifiées'), t('20+ live communities', 'Plus de 20 collectivités en production'), t('In production since 2017', 'En production depuis 2017'), t('AI-powered with ATNA', 'Propulsé par l’IA ATNA')].map(bullet => <li key={bullet} className="flex items-center gap-3" data-id="element-2449">
                        <CheckIcon className="w-5 h-5 text-indigo shrink-0" strokeWidth={2} data-id="element-2450" />
                        <span className="font-sans text-base text-black/80" data-id="element-2451">
                          {bullet}
                        </span>
                      </li>)}
                  </ul>
                </div>

                {/* Right side — Form */}
                <div className="bg-white border border-black/10 rounded-2xl p-8 md:p-10 shadow-sm" data-id="element-2452">
                  <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2453">
                    <div data-id="element-2454">
                      <label htmlFor="firstName" className="block font-sans text-sm font-medium text-black/70 mb-2" data-id="element-2455">
                        {t('First Name *', 'Prénom *')}
                      </label>
                      <input id="firstName" name="firstName" type="text" required value={form.firstName} onChange={e => setForm({
                    ...form,
                    firstName: e.target.value
                  })} className="w-full px-4 py-3 border border-black/15 rounded-lg font-sans text-base text-black focus:outline-none focus:border-indigo focus:ring-1 focus:ring-indigo transition-colors" data-id="element-2456" />
                    </div>
                    <div data-id="element-2457">
                      <label htmlFor="email" className="block font-sans text-sm font-medium text-black/70 mb-2" data-id="element-2458">
                        {t('Work Email *', 'Courriel professionnel *')}
                      </label>
                      <input id="email" name="email" type="email" required value={form.email} onChange={e => setForm({
                    ...form,
                    email: e.target.value
                  })} className="w-full px-4 py-3 border border-black/15 rounded-lg font-sans text-base text-black focus:outline-none focus:border-indigo focus:ring-1 focus:ring-indigo transition-colors" data-id="element-2459" />
                      <ValidationError prefix="Email" field="email" errors={state.errors} className="mt-2 font-sans text-sm text-red-600" data-id="element-2460" />
                    </div>
                    <div data-id="element-2461">
                      <label htmlFor="organization" className="block font-sans text-sm font-medium text-black/70 mb-2" data-id="element-2462">
                        {t('Organization *', 'Organisation *')}
                      </label>
                      <input id="organization" name="organization" type="text" required value={form.organization} onChange={e => setForm({
                    ...form,
                    organization: e.target.value
                  })} className="w-full px-4 py-3 border border-black/15 rounded-lg font-sans text-base text-black focus:outline-none focus:border-indigo focus:ring-1 focus:ring-indigo transition-colors" data-id="element-2463" />
                    </div>
                    <div data-id="element-2464">
                      <label htmlFor="orgType" className="block font-sans text-sm font-medium text-black/70 mb-2" data-id="element-2465">
                        {t('Organization Type', "Type d'organisation")}{' '}
                        <span className="text-black/40" data-id="element-2466">
                          {t('(Optional)', '(Facultatif)')}
                        </span>
                      </label>
                      <select id="orgType" name="orgType" value={form.orgType} onChange={e => setForm({
                    ...form,
                    orgType: e.target.value
                  })} className="w-full px-4 py-3 border border-black/15 rounded-lg font-sans text-base text-black focus:outline-none focus:border-indigo focus:ring-1 focus:ring-indigo transition-colors bg-white appearance-none" data-id="element-2467">
                        <option value="" disabled data-id="element-2468">
                          {t('Select an option', 'Choisissez une option')}
                        </option>
                        <option value="cities" data-id="element-2469">
                          {t('Cities & Government', 'Villes et gouvernement')}
                        </option>
                        <option value="airports" data-id="element-2470">
                          {t('Airports & Transportation', 'Aéroports et transport')}
                        </option>
                        <option value="telcos" data-id="element-2471">
                          {t('Telcos & Media', 'Télécoms et médias')}
                        </option>
                        <option value="health" data-id="element-2472">
                          {t('Health Networks', 'Réseaux de santé')}
                        </option>
                        <option value="universities" data-id="element-2473">
                          {t('Universities', 'Universités')}
                        </option>
                        <option value="other" data-id="element-2474">{t('Other', 'Autre')}</option>
                      </select>
                    </div>

                    <ValidationError errors={state.errors} className="font-sans text-sm text-red-600" data-id="element-2475" />

                    <div className="pt-2" data-id="element-2476">
                      <button type="submit" disabled={!formValid || state.submitting} className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-black text-white text-base font-medium rounded-xl hover:bg-black/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2477">
                        {state.submitting ? t('Sending…', 'Envoi…') : t('Book My Demo', 'Réserver ma démo')}
                        {!state.submitting && <ArrowRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2478" />}
                      </button>
                      <div className="mt-4 text-center space-y-2" data-id="element-2479">
                        <p className="font-sans text-sm text-black/50" data-id="element-2480">
                          {t('Takes less than 30 seconds.', 'Moins de 30 secondes.')}
                        </p>
                        <p className="font-sans text-xs text-black/40" data-id="element-2481">
                          {t('By submitting this form you agree that BCITI may contact you regarding your demo request.', 'En soumettant ce formulaire, vous acceptez que BCITI vous contacte au sujet de votre demande de démo.')}
                        </p>
                      </div>
                    </div>
                  </form>
                </div>
              </motion.div>}

            {/* Message court affiché après l'envoi — l'étape de prise de RDV est masquée pour le moment. */}
            {showThanks && <motion.div key="thanks" {...fade} className="max-w-2xl mx-auto text-center" data-id="element-2482">
                <div className="w-16 h-16 rounded-full bg-indigo/10 flex items-center justify-center mx-auto mb-8" data-id="element-2483">
                  <CheckIcon className="w-8 h-8 text-indigo" strokeWidth={2} data-id="element-2484" />
                </div>
                <h2 className="font-serif text-4xl md:text-5xl font-normal text-black mb-4" data-id="element-2485">
                  {t('Thank you!', 'Merci !')}
                </h2>
                <p className="font-sans text-lg text-black/60 mb-12" data-id="element-2486">
                  {t('We received your request and will get back to you shortly.', 'Nous avons bien reçu votre demande et vous répondrons sous peu.')}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4 pt-8 border-t border-black/10" data-id="element-2487">
                  <Link to="/" className="font-sans text-sm font-medium text-black/60 hover:text-black transition-colors" data-id="element-2488">
                    {t('Back to Home', "Retour à l'accueil")}
                  </Link>
                  <span className="text-black/20" data-id="element-2489">•</span>
                  <Link to="/brossard" className="font-sans text-sm font-medium text-indigo hover:text-indigo/80 transition-colors" data-id="element-2490">
                    {t('Explore Brossard', 'Explorer Brossard')}
                  </Link>
                </div>
              </motion.div>}

            {showConfirmation && <motion.div key="step1" {...fade} className="max-w-4xl mx-auto text-center" data-id="element-2491">
                <div className="w-16 h-16 rounded-full bg-indigo/10 flex items-center justify-center mx-auto mb-8" data-id="element-2492">
                  <CalendarIcon className="w-8 h-8 text-indigo" strokeWidth={2} data-id="element-2493" />
                </div>
                <h2 className="font-serif text-4xl md:text-5xl font-normal text-black mb-4" data-id="element-2494">
                  {t("You're Booked.", "C'est réservé.")}
                </h2>
                <p className="font-sans text-lg text-black/60 mb-12" data-id="element-2495">
                  {t('Choose a convenient time.', 'Choisissez un moment qui vous convient.')}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start text-left" data-id="element-2496">
                  {/* Fake Calendar Embed Area */}
                  <div className="bg-black/[0.02] border border-black/10 rounded-2xl p-8 min-h-[400px] flex flex-col items-center justify-center text-center" data-id="element-2497">
                    <CalendarIcon className="w-10 h-10 text-black/20 mb-4" strokeWidth={1.5} data-id="element-2498" />
                    <div className="font-sans text-base font-medium text-black/60 mb-2" data-id="element-2499">
                      {t('Calendar Scheduling Area', 'Zone de prise de rendez-vous')}
                    </div>
                    <div className="font-sans text-sm text-black/40" data-id="element-2500">
                      {t('(Embed code would load here)', '(Le code d’intégration se chargerait ici)')}
                    </div>
                  </div>

                  {/* What you will see */}
                  <div className="pt-4" data-id="element-2501">
                    <div className="font-sans text-sm font-semibold tracking-[0.2em] text-black/45 mb-6" data-id="element-2502">
                      {t('WHAT YOU WILL SEE DURING YOUR DEMO:', 'CE QUE VOUS VERREZ PENDANT VOTRE DÉMO :')}
                    </div>
                    <ul className="space-y-4 mb-12" data-id="element-2503">
                      {[t('Real customer example (City of Brossard)', 'Exemple client réel (Ville de Brossard)'), t('Infrastructure overview', "Vue d'ensemble de l'infrastructure"), t('Communications platform', 'Plateforme de communications'), t('Citizen services', 'Services aux citoyens'), t('ATNA AI capabilities', "Capacités d'IA ATNA"), t('Recommended deployment path', 'Parcours de déploiement recommandé')].map(item => <li key={item} className="flex items-center gap-3" data-id="element-2504">
                          <CheckIcon className="w-5 h-5 text-indigo shrink-0" strokeWidth={2} data-id="element-2505" />
                          <span className="font-sans text-base text-black/80" data-id="element-2506">
                            {item}
                          </span>
                        </li>)}
                    </ul>

                    <div className="flex flex-wrap items-center gap-4 pt-8 border-t border-black/10" data-id="element-2507">
                      <Link to="/" className="font-sans text-sm font-medium text-black/60 hover:text-black transition-colors" data-id="element-2508">
                        {t('Back to Home', "Retour à l'accueil")}
                      </Link>
                      <span className="text-black/20" data-id="element-2509">•</span>
                      <Link to="/brossard" className="font-sans text-sm font-medium text-indigo hover:text-indigo/80 transition-colors" data-id="element-2510">
                        {t('Explore Brossard', 'Explorer Brossard')}
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </main>
    </div>;
}